import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Building2, Users, Mail, CheckCircle2, UserPlus,
  Send, Shield, Clock, Ban, Info, ShieldAlert,
  Settings, Save, UserCog, ChevronDown, ChevronUp
} from "lucide-react";
import { toast } from "sonner";
import { enterpriseService } from "../services/enterpriseService";

export default function AdminPage() {
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isLoadingOrg, setIsLoadingOrg] = useState(true);
  const [isInviting, setIsInviting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Org settings state
  const [orgName, setOrgName] = useState("");
  const [orgIndustry, setOrgIndustry] = useState("");
  const [orgCompanySize, setOrgCompanySize] = useState("");
  const [isSavingOrg, setIsSavingOrg] = useState(false);

  const [transferTargetId, setTransferTargetId] = useState("");
  const [isTransferring, setIsTransferring] = useState(false);

  useEffect(() => {
    loadOrgData();
  }, []);

  const loadOrgData = async () => {
    setIsLoadingOrg(true);
    try {
      const [orgData, membersData] = await Promise.all([
        enterpriseService.getOrganization().catch(() => null),
        enterpriseService.getMembers().catch(() => []),
      ]);

      if (orgData) {
        setOrg(orgData);
        setOrgName(orgData.name || "");
        setOrgIndustry(orgData.industry || "");
        setOrgCompanySize(orgData.companySize || "");

        const currentEmail = localStorage.getItem("userEmail");
        const currentMember = (Array.isArray(membersData) ? membersData : []).find(
          m => m.email === currentEmail
        );
        setIsAdmin(currentMember?.role === "ORG_ADMIN" || currentMember?.role === "ORG_OWNER");
        setCurrentUserId(currentMember?.id || null);
      }
      setMembers(Array.isArray(membersData) ? membersData : []);
    } catch {
      // Not in an org
    }

    try {
      const requestsData = await enterpriseService.getPendingRequests();
      setPendingRequests(Array.isArray(requestsData) ? requestsData : []);
    } catch {
      // Not admin
    }

    setIsLoadingOrg(false);
  };

  const handleSaveOrg = async () => {
    if (!orgName.trim()) {
      toast.error("Organization name is required");
      return;
    }
    setIsSavingOrg(true);
    try {
      const updated = await enterpriseService.updateOrganization({
        name: orgName.trim(),
        industry: orgIndustry.trim() || undefined,
        companySize: orgCompanySize || undefined,
      });
      setOrg(updated);
      toast.success("Organization settings updated");
    } catch (error) {
      toast.error(error.message || "Failed to update organization");
    } finally {
      setIsSavingOrg(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (members.some(m => m.email === inviteEmail)) {
      toast.error("This user is already a member");
      return;
    }

    setIsInviting(true);
    try {
      await enterpriseService.inviteMember({ email: inviteEmail });
      toast.success("Invitation sent to " + inviteEmail);
      setInviteEmail("");
      loadOrgData();
    } catch (error) {
      toast.error(error.message || "Failed to send invitation");
    } finally {
      setIsInviting(false);
    }
  };

  const handleApprove = async (membershipId) => {
    try {
      await enterpriseService.approveRequest(membershipId);
      toast.success("Request approved");
      loadOrgData();
    } catch (error) {
      toast.error(error.message || "Failed to approve request");
    }
  };

  const handleReject = async (membershipId) => {
    try {
      await enterpriseService.rejectRequest(membershipId);
      toast.success("Request rejected");
      loadOrgData();
    } catch (error) {
      toast.error(error.message || "Failed to reject request");
    }
  };

  const handleToggleRole = async (membershipId, currentRole) => {
    const newRole = currentRole === "ORG_ADMIN" ? "ORG_MEMBER" : "ORG_ADMIN";
    try {
      await enterpriseService.updateMemberRole(membershipId, newRole);
      toast.success("Member role updated to " + (newRole === "ORG_ADMIN" ? "Admin" : "Member"));
      loadOrgData();
    } catch (error) {
      toast.error(error.message || "Failed to update member role");
    }
  };

  const currentUserEmail = localStorage.getItem("userEmail");
  const isOwner = org?.ownerEmail === currentUserEmail || org?.ownerId === currentUserId;
  const activeMembers = members.filter(m => m.status === "ACTIVE");
  const pendingInvites = members.filter(m => m.status === "PENDING" && !m.userId);

  if (isLoadingOrg) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold">Admin</h1>
          <p className="text-muted-foreground mt-1">You are not part of an organization.</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold">Admin</h1>
          <p className="text-muted-foreground mt-1">Organization settings</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <ShieldAlert className="h-12 w-12 text-muted-foreground mx-auto" />
            <p className="text-muted-foreground">Only organization admins can access this page.</p>
            <p className="text-sm text-muted-foreground">Contact your admin to manage team settings.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Admin</h1>
          <p className="text-muted-foreground mt-1">
            Manage your organization, members, and invitations
          </p>
        </div>
      </div>

      <Tabs defaultValue="settings">
        <TabsList>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" /> Settings
          </TabsTrigger>
          <TabsTrigger value="members" className="flex items-center gap-2">
            <Users className="h-4 w-4" /> Members ({activeMembers.length})
          </TabsTrigger>
          <TabsTrigger value="requests" className="flex items-center gap-2">
            <Clock className="h-4 w-4" /> Requests ({pendingRequests.length})
          </TabsTrigger>
          <TabsTrigger value="invite" className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" /> Invite
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="h-5 w-5" />
                Organization Settings
              </CardTitle>
              <CardDescription>
                Update your organization name, industry, and company size
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="orgName">Organization Name *</Label>
                <Input
                  id="orgName"
                  placeholder="e.g. Acme Corp"
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="orgIndustry">Industry</Label>
                  <Input
                    id="orgIndustry"
                    placeholder="e.g. Financial Services"
                    value={orgIndustry}
                    onChange={e => setOrgIndustry(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="orgCompanySize">Company Size</Label>
                  <select
                    id="orgCompanySize"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={orgCompanySize}
                    onChange={e => setOrgCompanySize(e.target.value)}
                  >
                    <option value="">Select company size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-1000">201-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>
              </div>
              <div className="pt-2">
                <Button onClick={handleSaveOrg} disabled={isSavingOrg}>
                  {isSavingOrg ? "Saving..." : (<><Save className="h-4 w-4 mr-1" /> Save Changes</>)}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Organization Info</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Domain</span>
                  <p className="font-medium">@{org.domain || "Not set"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Total Members</span>
                  <p className="font-medium">{activeMembers.length}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Owner</span>
                  <p className="font-medium">{org.ownerName || org.ownerEmail}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Created</span>
                  <p className="font-medium">{org.createdAt ? new Date(org.createdAt).toLocaleDateString() : "N/A"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Team Members</CardTitle>
              <CardDescription>
                All members of your organization — admins can promote or demote roles
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activeMembers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No members yet. Invite team members to get started.
                </p>
              ) : (
                <div className="space-y-2">
                  {activeMembers.map(member => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {member.name || member.email}
                            {member.id === currentUserId && (
                              <span className="text-xs text-muted-foreground ml-2">(you)</span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {member.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {member.role === "ORG_OWNER" ? (
                          <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 border-purple-200">
                            <Shield className="h-3 w-3 mr-1" /> Owner
                          </Badge>
                        ) : member.role === "ORG_ADMIN" ? (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200">
                            <Shield className="h-3 w-3 mr-1" /> Admin
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Member</Badge>
                        )}
                        {member.id !== currentUserId && member.role !== "ORG_OWNER" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleRole(member.id, member.role)}
                            className="text-xs"
                          >
                            <UserCog className="h-3 w-3 mr-1" />
                            {member.role === "ORG_ADMIN" ? "Demote" : "Make Admin"}
                          </Button>
                        )}
                        {member.joinedAt && (
                          <span className="text-xs text-muted-foreground hidden md:inline">
                            Joined {new Date(member.joinedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {pendingInvites.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Pending Invitations</CardTitle>
                <CardDescription>
                  Invitations sent but not yet accepted
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {pendingInvites.map(member => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{member.email}</span>
                        <Badge variant="outline" className="text-xs">Pending</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {isOwner && (
          <Card className="border-amber-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-amber-700">
                <ShieldAlert className="h-5 w-5" />
                Transfer Ownership
              </CardTitle>
              <CardDescription>
                Transfer organization ownership to another admin. The current owner will become an admin.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="transferOwner">New Owner</Label>
                  <select
                    id="transferOwner"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={transferTargetId}
                    onChange={e => setTransferTargetId(e.target.value)}
                  >
                    <option value="">Select admin member...</option>
                    {activeMembers.filter(m => m.id !== currentUserId && (m.role === "ORG_ADMIN" || m.role === "ORG_OWNER")).map(m => (
                      <option key={m.id} value={m.userId || m.id}>{m.name || m.email}</option>
                    ))}
                  </select>
                </div>
                <Button
                  variant="outline"
                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  disabled={!transferTargetId || isTransferring}
                  onClick={async () => {
                    if (!confirm("Are you sure you want to transfer ownership? This action cannot be undone.")) return;
                    setIsTransferring(true);
                    try {
                      await enterpriseService.transferOwnership(transferTargetId);
                      toast.success("Ownership transferred successfully");
                      setTransferTargetId("");
                      loadOrgData();
                    } catch (e) {
                      toast.error(e.message || "Failed to transfer ownership");
                    } finally {
                      setIsTransferring(false);
                    }
                  }}
                >
                  {isTransferring ? "Transferring..." : "Transfer Ownership"}
                </Button>
              </div>
              {activeMembers.filter(m => m.id !== currentUserId && (m.role === "ORG_ADMIN" || m.role === "ORG_OWNER")).length === 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  No other admin members available. Promote a member to admin first.
                </p>
              )}
            </CardContent>
          </Card>
          )}
        </TabsContent>

        <TabsContent value="requests" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Join Requests</CardTitle>
              <CardDescription>
                Members requesting to join your organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingRequests.length === 0 ? (
                <div className="text-center py-8 space-y-2">
                  <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto" />
                  <p className="text-sm text-muted-foreground">No pending requests.</p>
                  <p className="text-xs text-muted-foreground">
                    When someone from your domain requests to join, their request will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingRequests.map(req => (
                    <div key={req.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <UserPlus className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {req.name || req.email}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {req.email} · Requested to join
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(req.id)}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleReject(req.id)}
                        >
                          <Ban className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invite" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Invite Team Members</CardTitle>
              <CardDescription>
                Send an invitation email to join your organization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {org.domain && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Only users with @{org.domain} email addresses can be invited.
                  </AlertDescription>
                </Alert>
              )}
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label htmlFor="inviteEmail" className="sr-only">Email Address</Label>
                  <Input
                    id="inviteEmail"
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && (e.preventDefault(), handleInvite())}
                  />
                </div>
                <Button onClick={handleInvite} disabled={isInviting}>
                  {isInviting ? "Sending..." : (<><Send className="h-4 w-4 mr-1" /> Send Invite</>)}
                </Button>
              </div>

              {org.domain && (
                <p className="text-xs text-muted-foreground">
                  An email will be sent with instructions to join your organization.
                  Existing users can log in, new users will be guided through registration.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
